import { useEffect, useState } from 'react';
import { Loader, useCurrentUser } from '@beholos/holoscommon';
import { initMercadoPago } from '@mercadopago/sdk-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { CircleAlert, IdCard, InfoIcon, Snowflake } from 'lucide-react';
import SideBar from '../../components/global/SideBar';
import { ManagePaymentMethodsModal } from '../../components/membership/ManagePaymentMethodsModal';
import MembershipTabs from '../../components/membership/MembershipTabs';
import { UpdatePaymentMethodModal } from '../../components/membership/UpdatePaymentMethodModal';
import { usePageTitle } from '../../hooks/usePageTitle';
import './index.css';

export default function Membership() {
  usePageTitle('Mi Plan - Holos');

  useEffect(() => {
    initMercadoPago(import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY);
  }, []);

  const [isOpenUpdatePayment, setIsOpenUpdatePayment] = useState(false);
  const [isOpenManagePaymentMethods, setIsOpenManagePaymentMethods] = useState(false);

  const { user, loading, refetch } = useCurrentUser();
  const {
    freezeStart,
    freezeEnd,
    freezeStartPassed,
    freezeEndPassed,
    unFreezeDate,
    changePlan,
    cancelled,
    payed,
    manualResetDate,
    defaultPaymentMethodId,
  } = user || {};

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden h-full overflow-y-auto md:block md:w-80 lg:w-96">
        <SideBar />
      </div>

      <div className="flex-1 h-full overflow-y-auto bg-gray-200 px-6 py-15">
        <div className="mx-auto max-w-6xl rounded bg-gray-200 p-6">
          <div className="mb-6 text-left">
            <h3 className="mb-3 text-2xl font-medium font-juana md:text-4xl">
              <span>Mi plan</span>
            </h3>
          </div>

          {user?.hasPlan && user?.isCorporate && <MembershipTabs />}

          {loading ? (
            <Loader />
          ) : !user?.hasPlan ? (
            <section className="bg-white border border-gray-200 rounded-2xl py-8 px-6 my-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
                <div className="shrink-0 text-gray-700">
                  <IdCard size={40} />
                </div>

                <div className="flex-1">
                  <h4 className="text-lg md:text-xl font-bold font-juana text-black mb-1">
                    {user?.isCorporate ? 'Renueva tu plan' : 'Los planes Holos se obtienen a través de tu empresa'}
                  </h4>

                  {user?.isCorporate && (
                    <p className="text-gray-600 text-base md:text-md mb-4">
                      <strong>Aún no tienes un plan asignado por tu empresa.</strong>
                      <br />
                      Para acceder a los beneficios, por favor contacta al administrador de tu compañía y solicita que te asignen un plan base.
                    </p>
                  )}

                  {!user?.isCorporate && (
                    <>
                      <p className="text-gray-600 text-base md:text-md mb-4">
                        <strong>Por ahora no es posible adquirir un plan de forma individual.</strong>
                        <br />
                        Los planes Holos solo están disponibles a través de tu empresa. Sugiérenos tu empresa y nos pondremos en contacto para que tú y tu equipo puedan acceder a los beneficios.
                      </p>

                      <a
                        href="/para-empresas/referidos"
                        className="inline-block text-white bg-black border border-black px-6 py-2 rounded-full hover:bg-black/80 transition-colors cursor-pointer mt-2"
                      >
                        Sugerir mi empresa
                      </a>
                    </>
                  )}
                </div>
              </div>
            </section>
          ) : (
            <div className="space-y-6">
              {freezeStart ? (
                <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm">
                  <Snowflake size={25} />

                  <div>
                    {!freezeStartPassed ? (
                      <div>
                        Tu plan se congelará desde la fecha
                        <span className="capitalize font-bold"> {freezeStart} </span>
                        hasta la fecha
                        <span className="capitalize font-bold"> {freezeEnd}</span>
                      </div>
                    ) : freezeEndPassed ? (
                      <div>
                        Tu plan se encuentra activo y puedes reservar clases. Debido a que
                        realizaste un "freeze". Tu nueva fecha de
                        {Boolean(manualResetDate) ? ' acabar' : ' renovación automática'} es
                        <span className="capitalize font-bold font-extrabold">
                          {unFreezeDate && format(parseISO(unFreezeDate), 'MMMM dd, yyyy', { locale: es })}
                        </span>
                      </div>
                    ) : (
                      <div>
                        Tu plan está congelado desde <span className="capitalize">{freezeStart} </span>
                        hasta la fecha <span className="capitalize">{freezeEnd}</span>
                        <br />
                        Eso quiere decir que ahora tu Fitness Pass
                        se <strong>{Boolean(manualResetDate) ? 'acabará' : 'renovará'}</strong> automáticamente el
                        día <span className="uppercase font-bold font-extrabold">
                          <span className="capitalize">
                            {unFreezeDate && format(parseISO(unFreezeDate), 'MMMM dd, yyyy', { locale: es })}
                          </span>
                        </span>.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {changePlan && (
                <div className="flex items-center gap-2 rounded-lg border border-holos-dark bg-holos p-4 text-xs text-black">
                  <InfoIcon size={25} />
                  Tu plan cambiará a {changePlan.name} el:
                  <strong>{changePlan.date && format(parseISO(changePlan.date), 'dd/MM/yyyy')}</strong>
                </div>
              )}

              {payed === false ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm">
                  <div className="mb-3 flex items-start gap-3">
                    <CircleAlert size={25} className="mt-0.5 shrink-0" />

                    <div>
                      <p className="mb-1 text-base font-bold">Hubo un problema con tu pago</p>
                      <p className="m-0 leading-snug">
                        No pudimos procesar el pago de tu suscripción. Por favor, actualiza tu método de pago para reactivar tu plan. Si necesitas ayuda,{' '}
                        <a className="text-holos underline hover:text-holos/80" href="mailto:contacto@beholos.com">contáctanos</a>.
                      </p>
                    </div>
                  </div>

                  <button
                    className="mt-2 rounded bg-black px-6 py-2 text-white transition-colors hover:bg-gray-800 cursor-pointer"
                    onClick={() => setIsOpenUpdatePayment(true)}
                  >
                    Actualizar método de pago
                  </button>
                </div>
              ) : null}

              {cancelled ? (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm">
                  <CircleAlert size={25} />
                  Tu plan ha sido cancelado exitosamente, podrás utilizarlos hasta el:
                  <strong>{format(new Date(cancelled), 'dd/MM/yyyy')}</strong>
                </div>
              ) : null}

              <section className="my-6 flex flex-col overflow-hidden rounded-3xl border-2 border-gray-400 bg-white md:flex-row">
                <div className="flex items-center rounded-t-3xl border-b-2 border-gray-400 bg-holos-light p-5 md:rounded-l-3xl md:rounded-tr-none md:border-b-0 md:border-r-2 md:p-6">
                  <div>
                    <div className="inline-block rounded-4xl border border-gray-600 bg-prosper/70 px-4 py-1 font-medium">
                      {user?.hasPlan ? 'Activa' : 'Inactiva'}
                    </div>

                    {user?.plan?.sharedPassPlan ? (
                      <div className="my-2 break-words border-b-2 border-gray-400 pb-4 text-2xl font-medium md:pb-7 md:text-4xl">
                        Pase de invitados
                      </div>
                    ) : (
                      <div className="my-2 break-words border-b-2 border-gray-400 pb-4 text-2xl font-medium md:pb-7 md:text-4xl">
                        {user?.plan?.name}
                      </div>
                    )}

                    {!user?.plan?.sharedPassPlan && (
                      <div className={user?.plan?.basePlan?.price ? 'text-base font-bold text-gray-400 line-through md:text-xl' : 'text-base font-bold md:text-xl'}>
                        S/. {user?.plan?.price} por ciclo
                      </div>
                    )}

                    {user?.plan?.basePlan?.price && (
                      <div className="text-base font-bold md:text-xl">
                        S/. {user?.plan?.price - user?.plan?.basePlan?.price} por ciclo
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 p-5 md:p-6">
                  <div className="inner w-full text-black" style={{ maxWidth: '600px' }}>
                    <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <div className="text-sm text-gray-600">Nombre y Apellido:</div>
                        <div className="font-bold">{user?.firstname} {user?.lastname}</div>
                      </div>

                      <div>
                        <div className="text-sm text-gray-600">Estado del plan:</div>
                        <div className="font-bold">{user?.hasPlan ? 'Activa' : 'Inactiva'}</div>
                      </div>

                      <div>
                        <div className="text-sm text-gray-600">Fecha de renovación:</div>
                        <div className="font-bold">{user?.nextCycle ? format(parseISO(user.nextCycle), 'dd/MM/yyyy') : 'N/A'}</div>
                      </div>
                    </div>

                    <hr className="my-4 border-gray-300" />

                    <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="col-span-full">
                        <div className="mb-2 flex items-center text-sm font-bold">
                          <img
                            src="https://cache.beholos.com/img/holos/profile/plan-icon3.svg"
                            className="mr-2 inline-block"
                            style={{ maxWidth: '30px' }}
                            alt="Plan icon"
                          />
                          Plan elegido
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-gray-600">Plan elegido por mi:</div>
                        {user?.plan?.sharedPassPlan ? (
                          <div className="font-bold">Pase de invitados</div>
                        ) : (
                          <div className="font-bold">{user?.plan?.name}</div>
                        )}
                      </div>
                    </div>

                    {payed !== false && user.isCorporate && (
                      <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center md:mt-10 md:gap-4">
                        <a
                          href="/user/mi-membresia/administrar"
                          className="btn w-full cursor-pointer rounded-full border border-black bg-black px-6 py-2 text-center text-white transition-colors hover:bg-black/80 sm:w-auto"
                        >
                          Mejorar mi plan
                        </a>

                        {payed === true && (
                          <button
                            type="button"
                            onClick={() => setIsOpenManagePaymentMethods(true)}
                            className="btn w-full cursor-pointer rounded-full border border-black bg-white px-6 py-2 text-center text-black transition-colors hover:bg-gray-100 sm:w-auto"
                          >
                            Administrar métodos de pago
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {user?.hasPlan && !user?.cancelled && !user?.changePlan && !user?.manualResetDate && payed !== false && (
                <div className="my-6 flex-grow rounded-2xl border-2 border-gray-400 bg-white p-6">
                  <div id="cancel-membership">
                    <h4 className="mb-3 text-2xl font-medium font-juana">Cancelar plan Holos</h4>

                    <div className="mb-3">
                      <p className="text-sm text-gray-600">
                        Puedes cancelar tu plan Holos en cualquier momento hasta, por lo menos, 48 horas antes de tu fecha de renovación para evitar el cargo automático SOLO del próximo ciclo. Tu ciclo actual no se verá afectado y seguirá vigente hasta el día que termine dicho periódo.
                        <a className="text-blue-500 underline hover:text-holos-dark" href="/terminos" target="_blank"> Ver Mas.</a>
                      </p>
                    </div>

                    <a
                      className="inline-block rounded bg-black px-6 py-2 text-white transition-colors hover:bg-gray-800"
                      href="/user/mi-membresia/cancelar"
                    >
                      Cancelar plan
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <UpdatePaymentMethodModal
        isOpen={isOpenUpdatePayment}
        closeModal={() => setIsOpenUpdatePayment(false)}
        refetch={refetch}
        chargeAmount={(user?.plan?.price || 0) - (user?.plan?.basePlan?.price || 0)}
      />

      <ManagePaymentMethodsModal
        isOpen={isOpenManagePaymentMethods}
        closeModal={() => setIsOpenManagePaymentMethods(false)}
        defaultPaymentMethodId={defaultPaymentMethodId}
        refetch={refetch}
      />
    </div>
  );
}